import { Injectable } from '@angular/core';
import {
	BehaviorSubject,
	Observable,
	Subject,
	catchError,
	filter,
	firstValueFrom,
	from,
	map,
	of,
	shareReplay,
	skip,
	tap,
} from 'rxjs';
import {
	CognitoUser,
	Role,
	TokenPack,
	CognitoCreateAccountPayload,
	CognitoCreateStudentIdAccountResponse,
	CognitoCreateEmailAccountResponse,
	RefreshTokenRequestPayload,
	_,
} from '@mira-avant/mtlibs';
import { ApiService } from './api.service';
import { LoggedInService } from './logged-in.service';
import { InProgressService } from './in-progress.service';

type TokenPackCodeResponse = {
	success: boolean;
	code?: string;
	error?: string;
};

@Injectable({
	providedIn: 'root',
})
export class AuthService {
	public userBs$ = new BehaviorSubject<CognitoUser | null>(null);
	public user$: Observable<CognitoUser> = this.userBs$.pipe(
		filter((user: CognitoUser | null) => user !== null && user !== undefined)
	) as Observable<CognitoUser>;

	private tokenPackSubject = new BehaviorSubject<TokenPack | null>(null);
	public tokenPack$ = this.tokenPackSubject.asObservable().pipe(skip(1), shareReplay(1));
	public token$ = this.tokenPack$.pipe(map(tokenPack => tokenPack?.token ?? null));
	private tokenRefreshCodeSubject = new Subject<string>();
	public tokenRefreshCode$ = this.tokenRefreshCodeSubject.asObservable();

	public loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

	public canWeDeleteInProgressCourseWork$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

	private isTokenRefreshInProgress: boolean = false;
	private includeTokenPackCodeOnRefresh: boolean = false;

	constructor(
		private api: ApiService,
		private loggedInService: LoggedInService,
		private inProgressService: InProgressService
	) {
		this.loadExistingToken$().subscribe();

		this.loggedInService.isLoggedIn$.subscribe(isLoggedIn => {
			if (!isLoggedIn) {
				this.reset();
			}
		});

		this.tokenPack$.subscribe(tokenPack => {
			if (!tokenPack) {
				return;
			}

			if (tokenPack.cognitoUser?.groups.includes(Role.ADMIN)) {
				this.inProgressService.canDeleteInProgressCourseWork$().then(canDelete => {
					this.canWeDeleteInProgressCourseWork$.next(canDelete);
				});
			}
		});
	}

	public async getSession(): Promise<TokenPack> {
		const response = await firstValueFrom(this.api.get<TokenPack>('cognito/session'));

		if (!response.success || !response.cognitoUser) {
			throw new Error(response.error || 'Session not authenticated');
		}

		return response;
	}

	/**
	 * Check if the token is valid on boot. If it is, set the user info and
	 * logged in state. If not, reset the user info and logged in state.
	 *
	 * @private
	 * @async
	 * @returns {*}
	 */
	private loadExistingToken$(): Observable<TokenPack | void> {
		return from(this.getSession()).pipe(
			tap(tokenPack => {
				this.setUserInfo(tokenPack);
			}),
			catchError((error: unknown) => {
				console.warn('No active session on boot', error);
				this.reset();
				this.tokenPackSubject.next(null);
				this.loggedInService.logout(true);
				return of(void 0);
			})
		);
	}

	public bindRefreshEvents(element: HTMLElement) {
		//Debounce events we want to listen to in order to trigger a token refresh.
		const debouncedRefreshToken = _.debounce(() => this.refreshTokenIfNeeded$(), 1000);

		//Attach the debounced function to both click and keyup events..
		element.addEventListener('click', debouncedRefreshToken, true);
		element.addEventListener('keyup', debouncedRefreshToken, true);
	}

	public setIncludeTokenPackCodeOnRefresh(includeTokenPackCodeOnRefresh: boolean): void {
		this.includeTokenPackCodeOnRefresh = includeTokenPackCodeOnRefresh;
	}

	public async refreshTokenIfNeeded$(): Promise<void> {
		try {
			const tokenPack = this.getCurrentTokenPack();

			if (!tokenPack?.tokenExpirationDate || !tokenPack.tokenDurationInMinutes) {
				return;
			}

			if (this.isTokenRefreshInProgress) {
				console.warn('Token refresh already in progress, skipping check.');
				return;
			}

			this.isTokenRefreshInProgress = true;

			const tokenExpirationDate = new Date(tokenPack.tokenExpirationDate);
			const tokenDurationInMinutes = tokenPack.tokenDurationInMinutes;
			const now = new Date();
			const halfWayToExpiration = new Date(
				tokenExpirationDate.getTime() - (tokenDurationInMinutes / 2) * 60 * 1000
			);
			const timeUntilExpirationInMinutes = (tokenExpirationDate.getTime() - now.getTime()) / (1000 * 60);
			const timeUntilRefreshInMinutes = (halfWayToExpiration.getTime() - now.getTime()) / (1000 * 60);
			const shouldRefreshToken = now.getTime() >= halfWayToExpiration.getTime();

			const notes = {
				Refreshed: shouldRefreshToken ? 'yes' : 'no',
				Duration: `${tokenDurationInMinutes}m`,
				'Time Until Expiration': `${timeUntilExpirationInMinutes.toFixed(2)}m`,
				'Time Until Refresh': `${timeUntilRefreshInMinutes.toFixed(2)}m`,
				Expires: tokenPack.tokenExpirationDate,
				HalfWay: halfWayToExpiration.toISOString(),
			};

			if (shouldRefreshToken) {
				await this.refreshToken();
			}

			console.table(notes);
		} catch (error) {
			console.warn('Error checking token expiration:', error);

			try {
				await this.refreshToken();
			} catch (refreshError: unknown) {
				const err = refreshError as Error;
				console.warn('Error refreshing token:', err);

				if (err?.message?.includes('refresh token')) {
					this.reset();
				}
			}
		} finally {
			this.isTokenRefreshInProgress = false;
		}
	}

	private async withLoading<T>(operation: () => Promise<T>): Promise<T> {
		this.loading$.next(true);
		try {
			return await operation();
		} finally {
			this.loading$.next(false);
		}
	}

	async login(username: string, password: string): Promise<TokenPack> {
		return this.withLoading(async () => {
			const result = await firstValueFrom(
				this.api.post<TokenPack>('cognito/login', {
					username,
					password,
				})
			);

			return await this.loginFromTokenPack(result);
		});
	}

	async checkForChallenge(username: string, password: string): Promise<TokenPack> {
		return this.withLoading(async () => {
			const result = await firstValueFrom(
				this.api.post<TokenPack>('cognito/challenge', {
					username,
					password,
				})
			);

			return result;
		});
	}

	async loginFromTokenPack(tokenPack: TokenPack): Promise<TokenPack> {
		if (!tokenPack || !tokenPack.cognitoUser) {
			throw new Error('Invalid token pack');
		}

		if (tokenPack.success && tokenPack.cognitoUser) {
			this.setUserInfo(tokenPack);
		}

		return tokenPack;
	}

	async createAccount(
		subdomain: string,
		username: string,
		sectionCodes: string,
		firstName?: string,
		lastName?: string
	): Promise<CognitoCreateEmailAccountResponse | CognitoCreateStudentIdAccountResponse> {
		return this.withLoading(async () => {
			const payload: CognitoCreateAccountPayload = {
				username,
				subdomain,
				sectionCodes,
				firstName,
				lastName,
			};

			return await firstValueFrom(this.api.post('cognito/create-account', payload));
		});
	}

	async verifyAccount(username: string, verificationCode: string): Promise<any> {
		return this.withLoading(async () => {
			return await firstValueFrom(this.api.post('cognito/verify-account', { username, verificationCode }));
		});
	}

	async resetPassword(username: string, verificationCode: string, newPassword: string): Promise<any> {
		return this.withLoading(async () => {
			return await firstValueFrom(
				this.api.post('cognito/reset-password', {
					username,
					verificationCode,
					newPassword,
				})
			);
		});
	}

	async forgotPassword(username: string): Promise<any> {
		return this.withLoading(async () => {
			return await firstValueFrom(this.api.post('cognito/forgot-password', { username }));
		});
	}

	async refreshToken(): Promise<TokenPack> {
		return this.withLoading(async () => {
			const tokenPack = this.getCurrentTokenPack();
			const payload: RefreshTokenRequestPayload = {
				includeTokenPackCode: this.includeTokenPackCodeOnRefresh,
			};

			const response = await firstValueFrom(this.api.post<TokenPack>('cognito/refresh-token', payload));

			if (response.success) {
				this.updateTokenPack({
					...response,
					cognitoUser: tokenPack?.cognitoUser,
				});

				if (response.tokenPackCode) {
					this.tokenRefreshCodeSubject.next(response.tokenPackCode);
				}
			}

			return response;
		});
	}

	async createTokenPackCode(): Promise<string> {
		const response = await firstValueFrom(this.api.post<TokenPackCodeResponse>('cognito/token-pack-code', {}));

		if (!response.success || !response.code) {
			throw new Error(response.error || 'Failed to create token pack code');
		}

		return response.code;
	}

	getCurrentTokenPack(): TokenPack | null {
		return this.tokenPackSubject.value;
	}

	getToken(): string | null {
		return this.tokenPackSubject.value?.token || null;
	}

	public async getExistingOrRefreshedToken(): Promise<string> {
		const token = this.getToken();

		if (token) {
			return token;
		}

		const tokenAfterBootCheck = await firstValueFrom(this.token$);

		if (tokenAfterBootCheck) {
			return tokenAfterBootCheck;
		}

		try {
			await this.refreshToken();
			const refreshedToken = this.getToken();

			if (!refreshedToken) {
				throw new Error('Token refresh failed');
			}

			return refreshedToken;
		} catch (error) {
			this.loggedInService.logout();
			console.error('Token expired. Please log in again.');
			return Promise.reject(error);
		}
	}

	private setUserInfo(tokenPack: TokenPack) {
		if (!tokenPack.cognitoUser) {
			throw new Error('Cognito user is missing');
		}

		this.userBs$.next(tokenPack.cognitoUser);

		this.loggedInService.loggedIn();
		this.tokenPackSubject.next(tokenPack);
	}

	private updateTokenPack(tokenPack: TokenPack) {
		const currentTokenPack = this.getCurrentTokenPack();

		if (!currentTokenPack) {
			throw new Error('Token pack is missing');
		}

		this.tokenPackSubject.next(tokenPack);
	}

	async getUserGroups(): Promise<string[]> {
		const user = await firstValueFrom(this.user$);
		return user ? user.groups : [];
	}

	async isInGroup$(group: string): Promise<boolean> {
		await firstValueFrom(this.loggedInService.isLoggedIn$.pipe(filter(isLoggedIn => isLoggedIn === true)));

		const userGroups = await this.getUserGroups();

		return userGroups.includes(group);
	}

	public async getHighestPermission$(): Promise<Role> {
		if (await this.isAdmin$()) {
			return Role.ADMIN;
		}

		if (await this.isClientAdmin$()) {
			return Role.CLIENT_ADMIN;
		}

		if (await this.isDistrictAdmin$()) {
			return Role.DISTRICT_ADMIN;
		}

		if (await this.isDemo$()) {
			return Role.DEMO;
		}

		if (await this.isSchoolAdmin$()) {
			return Role.SCHOOL_ADMIN;
		}

		if (await this.isTeacher$()) {
			return Role.TEACHER;
		}

		if (await this.isStudent$()) {
			return Role.STUDENT;
		}

		throw new Error('User does not have a valid role');
	}

	public async isHighestRoleAdmin$(): Promise<boolean> {
		return (await this.getHighestPermission$()) === Role.ADMIN;
	}

	public async isHighestRoleClientAdmin$(): Promise<boolean> {
		return (await this.getHighestPermission$()) === Role.CLIENT_ADMIN;
	}

	public async isHighestRoleDistrictAdmin$(): Promise<boolean> {
		return (await this.getHighestPermission$()) === Role.DISTRICT_ADMIN;
	}

	public async isHighestRoleSchoolAdmin$(): Promise<boolean> {
		return (await this.getHighestPermission$()) === Role.SCHOOL_ADMIN;
	}

	public async isHighestRoleTeacher$(): Promise<boolean> {
		return (await this.getHighestPermission$()) === Role.TEACHER;
	}

	public async isHighestRoleDemo$(): Promise<boolean> {
		return (await this.getHighestPermission$()) === Role.DEMO;
	}

	public async isHighestRoleStudent$(): Promise<boolean> {
		return (await this.getHighestPermission$()) === Role.STUDENT;
	}

	async isAdmin$(): Promise<boolean> {
		return await this.isInGroup$(Role.ADMIN);
	}

	async isTeacher$(): Promise<boolean> {
		return await this.isInGroup$(Role.TEACHER);
	}

	async isDemo$(): Promise<boolean> {
		return await this.isInGroup$(Role.DEMO);
	}

	async isStudent$(): Promise<boolean> {
		return await this.isInGroup$(Role.STUDENT);
	}

	async isSchoolAdmin$(): Promise<boolean> {
		return await this.isInGroup$(Role.SCHOOL_ADMIN);
	}

	async isDistrictAdmin$(): Promise<boolean> {
		return await this.isInGroup$(Role.DISTRICT_ADMIN);
	}

	async isClientAdmin$(): Promise<boolean> {
		return await this.isInGroup$(Role.CLIENT_ADMIN);
	}

	/**
	 * Look at logged-in.service.ts for more info you should call
	 * loggedInService.logout() to log the user out.
	 *
	 * @private
	 */
	private reset() {
		//See logged-in.service.ts for more info
		if (this.userBs$.value !== null) {
			this.userBs$.next(null);
		}

		if (this.tokenPackSubject.value !== null) {
			this.tokenPackSubject.next(null);
		}
	}
}
